<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import AppShell from "./views/AppShell.vue";
import { useConfigStore } from "./stores/config";
import { useAuthStore } from "./stores/auth";

const configStore = useConfigStore();
const authStore = useAuthStore();
const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { state } = storeToRefs(configStore);
const { state: authState } = storeToRefs(authStore);
const requiresAuth = computed(() => route.meta.requiresAuth !== false);
const retrying = ref(false);
const canRender = computed(
  () => !requiresAuth.value || (authStore.isAuthenticated() && !retrying.value),
);
let retryTimer: ReturnType<typeof setInterval> | undefined;
let disposed = false;

async function retryAuth() {
  if (
    disposed ||
    retrying.value ||
    state.value.kind !== "ready" ||
    !requiresAuth.value ||
    (authState.value.kind !== "error" && authState.value.kind !== "unknown") ||
    document.visibilityState !== "visible"
  )
    return;

  retrying.value = true;
  try {
    // Run every guard again, including deployment mode and role checks.
    // A successful retry must not bypass authorization for the original URL.
    await router.replace({
      path: route.path,
      query: route.query,
      hash: route.hash,
      force: true,
    });
  } finally {
    retrying.value = false;
  }
}

onMounted(async () => {
  await configStore.load();
  await router.isReady();
  if (disposed) return;
  retryTimer = setInterval(() => void retryAuth(), 10_000);
  window.addEventListener("online", retryAuth);
  window.addEventListener("pageshow", retryAuth);
  document.addEventListener("visibilitychange", retryAuth);
  // Resolve current operator (if cookie present) so the shell and guards
  // don't flash a logged-out state during the first render.
  if (authState.value.kind === "unknown" && !requiresAuth.value) void authStore.probe();
  void retryAuth();
});

onBeforeUnmount(() => {
  disposed = true;
  clearInterval(retryTimer);
  window.removeEventListener("online", retryAuth);
  window.removeEventListener("pageshow", retryAuth);
  document.removeEventListener("visibilitychange", retryAuth);
});
</script>

<template>
  <AppShell v-if="state.kind === 'ready' && canRender" :config="state.config" />
  <div v-else-if="state.kind === 'error'" class="boot-error">
    <h1>Nythronix bootstrap failed</h1>
    <p>{{ state.message }}</p>
    <button type="button" @click="configStore.load">Retry</button>
  </div>
  <div v-else-if="state.kind !== 'ready'" class="boot-loading">
    <p>Loading configuration…</p>
  </div>
  <div v-else-if="authState.kind === 'error'" class="boot-error" role="status">
    <h1>{{ t("auth.recovery_title") }}</h1>
    <p>{{ t("auth.recovery_hint") }}</p>
    <button type="button" :disabled="retrying" @click="retryAuth">
      {{ t("auth.retry") }}
    </button>
  </div>
  <div v-else class="boot-loading" role="status">
    <p>{{ t("auth.checking") }}</p>
  </div>
</template>

<style scoped>
.boot-error,
.boot-loading {
  padding: 2rem;
  font-family: ui-monospace, "JetBrains Mono", monospace;
}
.boot-error h1 {
  margin-bottom: 0.5rem;
  color: #b94a48;
}
</style>
